/**
 * Canonical Sales Rep Roster
 * ──────────────────────────
 * Single source of truth for all sales-rep identities used anywhere
 * in sales analytics, commissions, scorecards, follow-ups, and
 * appointment results.
 *
 * Rules (enforced by src/test/canonicalRoster.test.ts):
 *   1. Every `salesRep`, `assignedRep`, and (sales-context) `assignedTo`
 *      value in seed/demo data MUST be a `name` from this roster.
 *   2. Never hardcode rep names in sales analytics surfaces — resolve
 *      through this file or the `useCurrentRep()` hook.
 *
 * Admin/persona names (e.g. ops_manager users in Settings or template
 * authors in documentTemplateEngine) are intentionally OUT of scope.
 */

export interface SalesRepProfile {
  id: string;
  name: string;
  region: string;
  homeBase: string;          // primary zip
  coverageZips: string[];    // zip prefixes this rep covers
  phone?: string;
  /** Optional scheduling hints — used by sourceIntelligence routing */
  availability?: ("AM" | "PM")[];
  scheduleDensity?: "light" | "moderate" | "heavy";
  existingJobZips?: string[];
}

export const SALES_REP_ROSTER: SalesRepProfile[] = [
  {
    id: "rep-wesley",
    name: "Wesley Woodfin",
    region: "Tampa Bay",
    homeBase: "33606",
    coverageZips: ["336", "335"],
    availability: ["AM", "PM"],
    scheduleDensity: "moderate",
    existingJobZips: ["33609", "33611"],
  },
  {
    id: "rep-kyle",
    name: "Kyle Schall",
    region: "Tampa Bay",
    homeBase: "33701",
    coverageZips: ["337", "336"],
    availability: ["AM", "PM"],
    scheduleDensity: "light",
    existingJobZips: ["33706"],
  },
  {
    id: "rep-lucas",
    name: "Lucas Schall",
    region: "Gulf Coast",
    homeBase: "34236",
    coverageZips: ["342", "343", "338"],
    availability: ["AM", "PM"],
    scheduleDensity: "moderate",
    existingJobZips: ["34238"],
  },
  {
    id: "rep-trent",
    name: "Trent Hill",
    region: "Southwest Florida",
    homeBase: "33901",
    coverageZips: ["339", "341"],
    availability: ["AM", "PM"],
    scheduleDensity: "light",
    existingJobZips: ["33908"],
  },
];

/** Convenience: array of canonical rep names. */
export const CANONICAL_REP_NAMES: readonly string[] = SALES_REP_ROSTER.map(r => r.name);

/** Set form for fast membership tests. */
export const CANONICAL_REP_NAME_SET: ReadonlySet<string> = new Set(CANONICAL_REP_NAMES);

/** Lookup by name. Returns undefined for unknown reps. */
export function getRepByName(name: string): SalesRepProfile | undefined {
  return SALES_REP_ROSTER.find(r => r.name === name);
}

/** Membership check used by tests and selectors. */
export function isCanonicalRep(name: string): boolean {
  return CANONICAL_REP_NAME_SET.has(name);
}

/**
 * Legacy rep names that must NEVER appear in sales-rep identity fields
 * or sales-rep-scoped source files. Enforced by canonicalRoster.test.ts.
 */
export const LEGACY_REP_NAMES: readonly string[] = [
  "Mike Johnson",
  "Sarah Chen",
  "Sarah Davis",
  "Tom Baker",
  "David Williams",
];
